import type { AiRecommendation } from '@cobliteam/hooks';
import Button from '@cobliteam/ui/components/Button';
import Carousel, { CarouselContent, CarouselItem, useCarousel } from '@cobliteam/ui/components/Carousel';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import useAiRecommendationCta from '../../../AiRecommendationBanner/useAiRecommendationCta.tsx';
import CriticalityTag from '../../../CriticalityTag';

const CARD_BORDER_GRADIENT = 'linear-gradient(90deg, #001BB8 0%, #9847EE 100%)';

const criticalityOrder: Record<string, number> = { critical: 0, urgent: 1, important: 2, suggestive: 3 };

const MAX_VISIBLE = 3;

interface AiRecommendationTasksProps {
  recommendations: AiRecommendation[];
  improvementRecommendations: AiRecommendation[];
  allCompleted: boolean;
}

const RecommendationCarouselCard = ({ recommendation, allCompleted }: { recommendation: AiRecommendation; allCompleted: boolean }) => {
  const { ctaButton } = useAiRecommendationCta({
    journeyId: recommendation.journeyId,
    component: 'ai-recommendation-tasks',
    aiPrompt: recommendation.aiPrompt,
  });

  return (
    <div
      className='relative flex flex-col overflow-hidden rounded-[22px] bg-white shadow-sm'
      style={{
        minHeight: '260px',
        border: '1.5px solid transparent',
        backgroundImage: `linear-gradient(white, white), ${CARD_BORDER_GRADIENT}`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      }}
    >
      {/* Subtle gradient wash */}
      <div className='flex flex-1 flex-col gap-4 px-4 pt-6 pb-4' style={{ background: 'linear-gradient(180deg, rgba(152,71,238,0.06) 0%, rgba(255,255,255,0) 60%)' }}>
        {/* Criticality tag */}
        {!allCompleted && recommendation.criticality && (
          <div className='flex items-center gap-1.5'>
            <CriticalityTag criticality={recommendation.criticality} />
          </div>
        )}

        {/* Title + description */}
        <div className='flex flex-1 flex-col gap-1.5'>
          <Typography variant='description-title'>{recommendation.journeyName}</Typography>
          <Typography variant='caption-default'>{recommendation.description}</Typography>
        </div>

        {/* Footer: clock tag + CTA */}
        <div className='mt-auto flex items-center justify-between gap-3'>
          {recommendation.estimatedMinutes > 0 ? (
            <Tag variant='outlined' color='default' label={`Estimativa: ${recommendation.estimatedMinutes} min`} icon={<Icon name='clock' size='xs' />} />
          ) : (
            <span />
          )}
          {!allCompleted && ctaButton}
        </div>
      </div>
    </div>
  );
};

const CarouselNav = () => {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();

  return (
    <div className='mt-4 flex items-center justify-between'>
      <Button variant='ghost' color='primary' size='sm' startIcon={<Icon name='arrow-left' size='xs' />} disabled={!canScrollPrev} onClick={scrollPrev}>
        Recomendação anterior
      </Button>
      <Button variant='ghost' color='primary' size='sm' endIcon={<Icon name='arrow-right' size='xs' />} disabled={!canScrollNext} onClick={scrollNext}>
        Próxima recomendação
      </Button>
    </div>
  );
};

const AiRecommendationTasks = ({ recommendations, improvementRecommendations, allCompleted }: AiRecommendationTasksProps) => {
  const sorted = [...recommendations, ...improvementRecommendations]
    .sort((a, b) => (criticalityOrder[a.criticality] ?? 99) - (criticalityOrder[b.criticality] ?? 99))
    .slice(0, MAX_VISIBLE);

  if (!sorted.length) {
    return (
      <div className='flex items-center gap-2 p-4 py-3'>
        <Icon name='circle-check' size='sm' className='text-emerald-500' />
        <Typography variant='description-list-default' color='secondary'>
          Todas as jornadas concluídas!
        </Typography>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3 p-3'>
      <Carousel opts={{ align: 'start' }} className='[&>div]:!rounded-none flex-col'>
        <CarouselContent className='ml-0'>
          {sorted.map((rec) => (
            <CarouselItem key={rec.journeyId} className='pl-0'>
              <RecommendationCarouselCard recommendation={rec} allCompleted={allCompleted} />
            </CarouselItem>
          ))}
        </CarouselContent>
        {sorted.length > 1 && <CarouselNav />}
      </Carousel>
    </div>
  );
};

export default AiRecommendationTasks;
